import { useLazyQuery } from '@apollo/client/react';
import { useContext, useEffect } from 'react';
import {
	type Exact,
	type GetProductConfigurationInput,
	type ConfigurationFilterInput,
	type GetProductConfigurationQuery,
	type GetProductConfigurationQueryVariables
} from '../../../client/__generated__/graphql-client-types';
import { AddToCartContext } from '../../contexts/add-to-cart/add-to-cart.context';
import { ProductFamilyContext } from '../../contexts/product-family/product-family.context';
import { VariantContext } from '../../contexts/variant/variant.context';
import { generateSelectionFromRequiredGroups } from '../../helpers/product-helper/configuration/product-configuration.helper';
import { useRouterLocation } from '../../hooks/routing/location.hook';
import { GET_PRODUCT_CONFIGURATION } from '../../queries/product/configuration/configuration.queries';

type UseConfigurationProps = {
	familyId?: number;
	queryFilters?: ConfigurationFilterInput;
};
/**
 * Fetches the configuration data of a product and updates the selection object with them
 */
export const useConfiguration = ({
	familyId = 0,
	queryFilters
}: UseConfigurationProps): {
	status: useLazyQuery.Result<
		GetProductConfigurationQuery,
		Exact<{
			input: GetProductConfigurationInput;
		}>,
		'empty' | 'complete' | 'streaming'
	>;
} => {
	const { productFamily } = useContext(ProductFamilyContext);
	const { currentVariant, selectedVariant } = useContext(VariantContext);
	const { search } = useRouterLocation();
	const queryParams = new URLSearchParams(search);
	const paramQuantity = queryParams.get('quantity');
	const { selection, setSelection } = useContext(AddToCartContext);
	const cartQuantity = Number(paramQuantity) || 1;
	const { id } = productFamily;

	const [loadConfig, status] = useLazyQuery<GetProductConfigurationQuery, GetProductConfigurationQueryVariables>(
		GET_PRODUCT_CONFIGURATION
	);

	// Load Product Configuration
	useEffect(() => {
		void loadConfig({
			variables: { input: { id: familyId, variantId: selectedVariant.id, filters: queryFilters } }
		});
	}, [selectedVariant.id]);

	// Product Configuration Ready
	useEffect(() => {
		// Product Configuration loaded
		if (status.data?.getProductConfiguration && (selection?.id !== selectedVariant.id || !selection)) {
			const data = {
				variantId: selectedVariant.id,
				variantPrice: selectedVariant.priceInfo.current,
				variantQuantity: cartQuantity
			};

			return setSelection(
				generateSelectionFromRequiredGroups(
					status.data.getProductConfiguration.required,
					{ ...data, id },
					currentVariant.bundleItems.length > 0
				)
			);
		}
	}, [status?.data?.getProductConfiguration]);

	return { status };
};
